// frontend/client/src/services/recaptcha.js
// FIXED: Ensures script is fully loaded before executing

/* global grecaptcha */

/**
 * Wait for reCAPTCHA script to load
 * @returns {Promise<boolean>}
 */
const waitForRecaptchaLoad = () => {
  return new Promise((resolve) => {
    // If already loaded, resolve immediately
    if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.enterprise) {
      console.log('✅ [reCAPTCHA] Script already loaded');
      resolve(true);
      return;
    }

    console.log('⏳ [reCAPTCHA] Waiting for script to load...');
    
    // Poll for script to load (max 10 seconds)
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds (100 * 100ms)
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.enterprise) {
        clearInterval(checkInterval);
        console.log(`✅ [reCAPTCHA] Script loaded after ${attempts * 100}ms`);
        resolve(true);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('❌ [reCAPTCHA] Script failed to load after 10 seconds');
        resolve(false);
      }
    }, 100);
  });
};

/**
 * Executes reCAPTCHA Enterprise verification for a given action.
 * @param {string} action The action being performed (e.g., 'LOGIN', 'SIGNUP', 'PASSWORD_RESET').
 * @returns {Promise<string|null>} The reCAPTCHA token, or null on failure.
 */
export const executeRecaptcha = async (action) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  console.log('🔍 [reCAPTCHA] Starting execution for action:', action);
  console.log('🔍 [reCAPTCHA] Site Key configured:', !!siteKey);
  console.log('🔍 [reCAPTCHA] Site Key preview:', siteKey ? siteKey.substring(0, 15) + '...' : 'MISSING');
  
  // Check if site key is configured
  if (!siteKey) {
    console.error('❌ [reCAPTCHA] ERROR: REACT_APP_RECAPTCHA_SITE_KEY not configured');
    return null;
  }

  // 🔧 FIX: Wait for script to load before proceeding
  const scriptLoaded = await waitForRecaptchaLoad();
  
  if (!scriptLoaded) {
    console.error('❌ [reCAPTCHA] Script failed to load');
    return null;
  }

  console.log('🔍 [reCAPTCHA] Window.grecaptcha exists:', typeof window.grecaptcha !== 'undefined');
  console.log('🔍 [reCAPTCHA] grecaptcha.enterprise exists:', !!window.grecaptcha.enterprise);
  console.log('🔍 [reCAPTCHA] grecaptcha.enterprise.execute exists:', !!window.grecaptcha.enterprise?.execute);
  console.log('🔍 [reCAPTCHA] grecaptcha.enterprise.ready exists:', !!window.grecaptcha.enterprise?.ready);
  
  return new Promise((resolve) => {
    // Double-check after waiting
    if (typeof window.grecaptcha === 'undefined') {
      console.error('❌ [reCAPTCHA] ERROR: grecaptcha object not found after waiting.');
      return resolve(null);
    }
    
    if (!window.grecaptcha.enterprise) {
      console.error('❌ [reCAPTCHA] ERROR: grecaptcha.enterprise not found after waiting.');
      return resolve(null);
    }

    // Use grecaptcha.enterprise.ready() to ensure it's fully initialized
    window.grecaptcha.enterprise.ready(async () => {
      try {
        console.log('✅ [reCAPTCHA] ready() callback fired - executing for action:', action);
        
        // 🔧 FIX: Add a small delay to ensure full initialization
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const token = await window.grecaptcha.enterprise.execute(siteKey, { 
          action: action.toUpperCase() 
        });
        
        console.log('✅ [reCAPTCHA] Token generated successfully');
        console.log('🔍 [reCAPTCHA] Token length:', token?.length);
        console.log('🔍 [reCAPTCHA] Token preview:', token ? token.substring(0, 30) + '...' : 'NULL');
        
        resolve(token);
      } catch (error) {
        console.error('❌ [reCAPTCHA] Execution error:', error);
        console.error('❌ [reCAPTCHA] Error name:', error.name);
        console.error('❌ [reCAPTCHA] Error message:', error.message);
        console.error('❌ [reCAPTCHA] Error stack:', error.stack);
        
        // Specific error handling
        if (error.message.includes('Invalid site key') || error.message.includes('sitekey')) {
          console.error('❌ [reCAPTCHA] CRITICAL: Site key is invalid or domain not authorized');
          console.error('❌ [reCAPTCHA] Site key being used:', siteKey);
          console.error('❌ [reCAPTCHA] Current domain:', window.location.hostname);
          console.error('❌ [reCAPTCHA] Action:');
          console.error('   1. Verify this EXACT key exists in Google Console');
          console.error('   2. Verify this domain is in the authorized list');
          console.error('   3. Wait 2-3 minutes after making changes');
        } else if (error.message.includes('Timeout')) {
          console.error('❌ [reCAPTCHA] reCAPTCHA execution timed out');
        } else if (error.message.includes('Network')) {
          console.error('❌ [reCAPTCHA] Network error - check CSP settings');
        }
        
        resolve(null);
      }
    });
  });
};

/**
 * Test function to verify reCAPTCHA is working
 */
export const testRecaptchaFromDocumentation = async () => {
  console.log('🧪 [reCAPTCHA] Testing with exact documentation pattern...');
  
  // Wait for script to load
  const scriptLoaded = await waitForRecaptchaLoad();
  
  if (!scriptLoaded) {
    console.error('❌ Test Failed: Script not loaded');
    return null;
  }
  
  return new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        console.log('🔄 [reCAPTCHA] Documentation test - executing with site key:', siteKey ? siteKey.substring(0, 15) + '...' : 'MISSING');
        
        const token = await window.grecaptcha.enterprise.execute(siteKey, {action: 'TEST'});
        
        console.log('✅ [reCAPTCHA] Documentation test PASSED');
        console.log('🔍 [reCAPTCHA] Test token length:', token?.length);
        console.log('🔍 [reCAPTCHA] Test token preview:', token ? token.substring(0, 30) + '...' : 'NULL');
        resolve(token);
      } catch (error) {
        console.error('❌ [reCAPTCHA] Documentation test FAILED:', error);
        console.error('❌ [reCAPTCHA] Test error details:', error.message);
        resolve(null);
      }
    });
  });
};

/**
 * Force reload reCAPTCHA script - useful for debugging
 */
export const forceRecaptchaReload = () => {
  return new Promise((resolve) => {
    console.log('🔄 [reCAPTCHA] Force reloading script...');
    
    // Remove existing script
    const existingScript = document.querySelector('script[src*="google.com/recaptcha"]');
    if (existingScript) {
      existingScript.remove();
      console.log('✅ [reCAPTCHA] Removed existing script');
    }
    
    // Create new script
    const script = document.createElement('script');
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    
    script.onload = async () => {
      console.log('✅ [reCAPTCHA] Script reloaded successfully');
      // Wait for it to be ready
      await waitForRecaptchaLoad();
      resolve(true);
    };
    
    script.onerror = (error) => {
      console.error('❌ [reCAPTCHA] Failed to reload script:', error);
      resolve(false);
    };
    
    document.head.appendChild(script);
  });
};